import { FC, useState, useContext, useMemo, useEffect } from "react";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { toast, ToastOptions } from "react-toastify";
import { ExportFormatType } from "@app/exports/enumerations";
import * as helpers from "@app/exports/export-panel-body/exportPanelBodyHelpers";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextArea } from "@app/shared/qes-standards/forms/TextArea";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { blockNonIntegerKeys, sanitizeIntegerInput } from "@app/shared/utility/positiveIntegerInput";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { DocumentTypes, DocumentTypesLabels } from "@app/admin/specialty-group-exception/documentTypesEnum";
import { CciioNewProviderFrequency, ExceptionDocumentType } from "@app/exceptions/enums";
import ExceptionsService from "@app/exceptions/exceptionsService";
import {
  ExportCmsExceptionReportRequestModel,
  ExportCciioJustificationReportRequestModel
} from "@app/exports/exportModels";

interface GenerateDocumentFlyoutProps {
  selectedExceptionIds: number[];
  resetSelectedCheckboxes: () => void;
  customProjectName: string[];
  enableRegulatoryExceptionReports: boolean[];
  haveRemovedServiceArea: boolean[];
  haveRemovedSpecialty: boolean[];
  refreshData: () => void;
  documentType: ExceptionDocumentType;
}

const GenerateDocumentFlyout: FC<GenerateDocumentFlyoutProps> = (props: GenerateDocumentFlyoutProps) => {
  const [emailOnSuccess, setEmailOnSuccess] = useState(true);
  const [rationaleQuestion1, setRationaleQuestion1] = useState<boolean | null>(null);
  const [rationaleQuestion2, setRationaleQuestion2] = useState<boolean | null>(null);
  const [rationaleQuestion3, setRationaleQuestion3] = useState<boolean | null>(null);
  const [rationaleQuestion4, setRationaleQuestion4] = useState<boolean | null>(null);
  const [newProviderSources, setNewProviderSources] = useState<string>("");
  const [newProviderFrequency, setNewProviderFrequency] = useState<CciioNewProviderFrequency | null>(null);
  const [qhpEnrolleesResponsibilityCheck, setQhpEnrolleesResponsibilityCheck] = useState<boolean | null>(null);
  const [qhpEnrolleeComplaints, setQhpEnrolleeComplaints] = useState<string>("");
  const [outOfNetworkPolicyUrl, setOutOfNetworkPolicyUrl] = useState<string>("");
  const [includeZeroPercentGainsProvidersData, setIncludeZeroPercentGainsProvidersData] = useState<boolean>(false);
  const [zeroPercentGainsProvidersComment, setZeroPercentGainsProvidersComment] = useState<string>("");
  const exceptionsService = new ExceptionsService();
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const flyoutContext = useContext(FlyoutContext);
  const isCciio = props.documentType === ExceptionDocumentType.CCIIO;
  const reportPrefix = isCciio ? "CCIIO_Justification" : "CMS_Exception_Report";
  const reportLabel = isCciio ? "CCIIO Justification form" : "CMS Exception Template";

  // removing MMP option since it's been discontinued, but putting it's removal behind a feature flag just in case we need to revert.
  // When the feature flag is removed, we can also remove the DocumentType.CmsMmp option and any related code.
  const documentTypes = isCciio
    ? [DocumentTypes.CciioJustificationSubmission]
    : featureFlags.has(Features.CCIIO_JUSTIFICATION_EXPORT)
      ? [DocumentTypes.CmsMa]
      : [DocumentTypes.CmsMa, DocumentTypes.CmsMmp];

  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentTypes>(
    isCciio ? DocumentTypes.CciioJustificationSubmission : DocumentTypes.CmsMa
  );

  const [zipFileName, setZipFileName] = useState<string>(helpers.reportName(reportPrefix));

  const handleGenerateDocument = async () => {
    if (!selectedDocumentType || !zipFileName || props.selectedExceptionIds.length === 0) return;

    if (isCciio) {
      await exceptionsService.bulkUpdateSpecialtyGroupExceptionsCciioAnswers({
        cciioNewProviderFrequency: newProviderFrequency,
        cciioNewProviderSources: newProviderSources || null,
        cciioOutOfNetworkPolicyUrl: outOfNetworkPolicyUrl || null,
        cciioQhpEnrolleeComplaints: qhpEnrolleeComplaints ? parseInt(qhpEnrolleeComplaints) : null,
        cciioQhpEnrolleesResponsibilityCheck: qhpEnrolleesResponsibilityCheck,
        hasDiscrepancies: false,
        selectedExceptionIds: props.selectedExceptionIds,
      });
    } else {
      await exceptionsService.bulkUpdateSpecialtyGroupExceptionsRationales({
          exceptionRationale1: rationaleQuestion1,
          exceptionRationale2: rationaleQuestion2,
          exceptionRationale3: rationaleQuestion3,
          exceptionRationale4: rationaleQuestion4,
          selectedExceptionIds: props.selectedExceptionIds,
        }
      );
    }

    await exceptionsService.bulkUpdateSpecialtyGroupZeroPercentGainsProviders({
        selectedExceptionIds: props.selectedExceptionIds,
        zeroPercentGainsProvidersComment: includeZeroPercentGainsProvidersData ? zeroPercentGainsProvidersComment : null,
      }
    );

    try {
      if (isCciio) {
        const payload: ExportCciioJustificationReportRequestModel = {
          eMailOnSuccess: emailOnSuccess,
          exportName: zipFileName,
          formatType: ExportFormatType.Excel,
          includeZeroPercentGainsProvidersData: includeZeroPercentGainsProvidersData,
          runAsynchronous: true,
          specialtyGroupExceptionIds: props.selectedExceptionIds,
          zeroPercentGainsProvidersComment: includeZeroPercentGainsProvidersData ? zeroPercentGainsProvidersComment : null,
        };
        await exceptionsService.exportCciioJustificationReport(payload);

      } else {
        const payload: ExportCmsExceptionReportRequestModel = {
          documentType: selectedDocumentType,
          eMailOnSuccess: emailOnSuccess,
          exceptionRationale1: rationaleQuestion1,
          exceptionRationale2: rationaleQuestion2,
          exceptionRationale3: rationaleQuestion3,
          exceptionRationale4: rationaleQuestion4,
          exportName: zipFileName,
          formatType: ExportFormatType.CompressedBinary,
          includeZeroPercentGainsProvidersData: includeZeroPercentGainsProvidersData,
          runAsynchronous: true,
          specialtyGroupExceptionIds: props.selectedExceptionIds,
          zeroPercentGainsProvidersComment: includeZeroPercentGainsProvidersData ? zeroPercentGainsProvidersComment : null,
        };

        await exceptionsService.exportCmsExceptionReport(payload);
      }

      toast.info(`${zipFileName} has been queued for generation. Please check email / Exception Reports page for status.`, {
        containerId: "z-index-1030",
      });
      props.resetSelectedCheckboxes();
      props.refreshData();
      flyoutContext.handleCloseFlyout();
      resetFlyoutState();
    } catch (error) {
      const toastOptions: ToastOptions = {
        closeOnClick: true,
        containerId: "z-index-9999",
      };
      toast.error("Error generating document", toastOptions);
    }
  };

  const resetFlyoutState = () => {
    setSelectedDocumentType(null);
    setZipFileName(helpers.reportName(reportPrefix));
    setEmailOnSuccess(true);
  };

  const alertMessage = !isCciio
    ? (<>Document will be downloaded as a .zip file.<br />Any supplemental documentation will be included in the export file.</>)
    : (<>Document will be downloaded as a .zip file.</>);

  const checkConditions = (conditions: boolean[]) => {
    const allTrue = !conditions.some(value => !value);
    const mixed = conditions.some(value => !value) && conditions.some(value => value);
    return { allTrue, mixed };
  };

  const getProjects = (conditions: boolean[], projectNames: string[]) => {
    return Array.from(new Set(projectNames.filter((_, index) => conditions[index])))
      .sort((a, b) => a.localeCompare(b));
  };

  const getSavedRationaleAnswers = async () => {
    try {
      const rationaleAnswers = await exceptionsService.getRationaleAnswersByExceptionIds(props.selectedExceptionIds);

      setRationaleQuestion1(rationaleAnswers.exceptionRationale1);
      setRationaleQuestion2(rationaleAnswers.exceptionRationale2);
      setRationaleQuestion3(rationaleAnswers.exceptionRationale3);
      setRationaleQuestion4(rationaleAnswers.exceptionRationale4);
    } catch (error) {
      {/*do nothing, user can still generate report without rationale answers*/ }
    }
  };

  const getSavedZeroPercentGainsProviders = async () => {
    const zeroPercentGainsProvidersComment = await exceptionsService.getZeroPercentGainsProvidersCommentByExceptionIds(props.selectedExceptionIds);
    setIncludeZeroPercentGainsProvidersData(zeroPercentGainsProvidersComment != null && zeroPercentGainsProvidersComment !== "");
    setZeroPercentGainsProvidersComment(zeroPercentGainsProvidersComment);
  };

  const getSavedCciioAnswers = async () => {
    try {
      const cciioAnswers = await exceptionsService.getCciioAnswersByExceptionIds(props.selectedExceptionIds);
      setNewProviderSources(cciioAnswers.cciioNewProviderSources ?? "");
      setNewProviderFrequency(cciioAnswers.cciioNewProviderFrequency);
      setQhpEnrolleesResponsibilityCheck(cciioAnswers.cciioQhpEnrolleesResponsibilityCheck);
      setQhpEnrolleeComplaints(cciioAnswers.cciioQhpEnrolleeComplaints?.toString() ?? "");
      setOutOfNetworkPolicyUrl(cciioAnswers.cciioOutOfNetworkPolicyUrl ?? "");
    } catch (error) {
      {/*do nothing, user can still generate report without CCIIO answers*/ }
    }
  };

  const { allTrue: insufficientPrivileges, mixed: mixedPrivileges } = checkConditions(props.enableRegulatoryExceptionReports.map(value => !value));
  const projectsWithInsufficientPrivileges = useMemo(() => getProjects(props.enableRegulatoryExceptionReports.map(value => !value), props.customProjectName), [props.customProjectName, props.enableRegulatoryExceptionReports]);

  const { allTrue: allHaveRemovedServiceArea, mixed: mixedHaveRemovedServiceArea } = checkConditions(props.haveRemovedServiceArea);
  const projectsWithRemovedServiceArea = useMemo(() => getProjects(props.haveRemovedServiceArea, props.customProjectName), [props.customProjectName, props.haveRemovedServiceArea]);

  const { allTrue: allHaveRemovedSpecialty, mixed: mixedHaveRemovedSpecialty } = checkConditions(props.haveRemovedSpecialty);
  const projectsWithRemovedSpecialty = useMemo(() => getProjects(props.haveRemovedSpecialty, props.customProjectName), [props.customProjectName, props.haveRemovedSpecialty]);

  const isQhpComplaintsValid = !qhpEnrolleeComplaints || (Number.isInteger(Number(qhpEnrolleeComplaints)) && Number(qhpEnrolleeComplaints) >= 0);
  const isOutOfNetworkUrlValid = !outOfNetworkPolicyUrl || (() => {
    try { const u = new URL(outOfNetworkPolicyUrl); return u.protocol === "http:" || u.protocol === "https:"; }
    catch { return false; }
  })();

  //check to see if any selected items meets criteria to generate document:
  //does not have removed service area && does not have removed specialty && has priveleges,
  const submitCriteriaCheckPassed = useMemo(() => {
    return props.customProjectName.some((_, index) => !props.haveRemovedServiceArea[index] && !props.haveRemovedSpecialty[index] && props.enableRegulatoryExceptionReports[index]);
  }, [props.customProjectName, props.haveRemovedSpecialty, props.haveRemovedServiceArea, props.enableRegulatoryExceptionReports]);

  useEffect(() => {
    if (!!props.selectedExceptionIds && props.selectedExceptionIds.length > 0) {
      if (isCciio) {
        getSavedCciioAnswers();
      } else {
        getSavedRationaleAnswers();
      }
      getSavedZeroPercentGainsProviders();
    }
  }, [props.selectedExceptionIds]);

  return (
    <>
      <div>
        <div className="row">
          <div className="col-10 mb-3">
            {mixedPrivileges && (
              <QESAlert
                type="warning"
                persist={true}
                collapseTitle={
                  <>
                    <strong>Unable to generate the {reportLabel}. Insufficient privileges for the following projects. Please contact Quest Client Services for assistance.</strong>
                  </>
                }
              >
                <ul>
                  {projectsWithInsufficientPrivileges.map((project, index) => (
                    <li key={index}>{project}</li>
                  ))}
                </ul>
              </QESAlert>
            )}
            {insufficientPrivileges && (
              <QESAlert classNames="mb-3" type="danger" persist={true}>
                <strong>Unable to generate the {reportLabel}. Insufficient privileges for all selected projects. Please contact Quest Client Services for assistance.</strong>
              </QESAlert>
            )}
            {mixedHaveRemovedServiceArea && (
              <QESAlert
                type="warning"
                persist={true}
                collapseTitle={
                  <>
                    <strong>Unable to generate the {reportLabel}. The following selected exceptions no longer have the service area in the project. Please check the service area in the project and try again.</strong>
                  </>
                }
              >
                <ul>
                  {projectsWithRemovedServiceArea.map((project, index) => (
                    <li key={index}>{project}</li>
                  ))}
                </ul>
              </QESAlert>
            )}
            {allHaveRemovedServiceArea && (
              <QESAlert classNames="mb-3" type="danger" persist={true}>
                <strong>Unable to generate the {reportLabel}. All selected exceptions no longer have the service area in the project. Please check the service area in the project and try again.</strong>
              </QESAlert>
            )}
            {mixedHaveRemovedSpecialty && (
              <QESAlert
                type="warning"
                persist={true}
                collapseTitle={
                  <>
                    <strong>Unable to generate the {reportLabel}. The following selected exceptions no longer have the specialty listed in the project. Please check your selected exception and try again.</strong>
                  </>
                }
              >
                <ul>
                  {projectsWithRemovedSpecialty.map((project, index) => (
                    <li key={index}>{project}</li>
                  ))}
                </ul>
              </QESAlert>
            )}
            {allHaveRemovedSpecialty && (
              <QESAlert classNames="mb-3" type="danger" persist={true}>
                <strong>Unable to generate the {reportLabel}. All selected exceptions no longer have the specialty listed in the project. Please check your selected exception and try again.</strong>
              </QESAlert>
            )}
            <QESAlert classNames="mb-3" type="info" persist={true}>
              <strong>{alertMessage}</strong>
            </QESAlert>
            <QESSelect
              value={selectedDocumentType || ""}
              onChange={(e) => setSelectedDocumentType(e.target.value as DocumentTypes)}
              placeholder="Select Document Type"
              label="Document Type"
              testId="exception__document-type-select"
              required={true}
              isValid={!!selectedDocumentType}
              allowEmpty={false}
            >
              {documentTypes.map(type => (
                <option key={type} value={type}>
                  {DocumentTypesLabels[type]}
                </option>
              ))}
            </QESSelect>
            <br />
            <QESTextInput
              value={zipFileName}
              onChange={(e) => setZipFileName(e.target.value)}
              placeholder="Enter Zip File Name"
              label="Zip File Name"
              testId="exception__zip-file-name-input"
              required={true}
              isValid={!!zipFileName}
            />
            <br />
            {!isCciio && (
              <>
                <div className="row mb-2">
                  <div className="col-12">
                    <strong>Please respond to the questions below by selecting either &quot;Yes&quot; or &quot;No&quot; from the drop-down list for each question.</strong>
                  </div>
                  <br />
                  <br />
                  <div className="col-9">
                    <label htmlFor="exception__rationale-question-1-select">
                      <strong>Question 1:</strong> Does the applicant attest that it has reviewed publicly available databases and other sources to determine availability of providers/facilities with respect to the exception being requested?
                    </label>
                  </div>
                  <div className="col-3 d-flex justify-content-end">
                    <QESSelect
                      value={rationaleQuestion1 == null ? "" : rationaleQuestion1 ? "true" : "false"}
                      onChange={e => setRationaleQuestion1(e.target.value === "" ? null : e.target.value === "true")}
                      id="exception__rationale-question-1-select"
                      testId="exception__rationale-question-1-select"
                      label=""
                      required={false}
                      isValid={!!rationaleQuestion1}
                      allowEmpty={true}
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </QESSelect>
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-9">
                    <label htmlFor="exception__rationale-question-2-select">
                      <strong>Question 2:</strong> If the applicant responded “yes” to Question 1, above, did the applicant’s review identify providers/ facilities within CMS’s network adequacy criteria, and with which the applicant has not contracted?
                    </label>
                  </div>
                  <div className="col-3 d-flex justify-content-end">
                    <QESSelect
                      value={rationaleQuestion2 == null ? "" : rationaleQuestion2 ? "true" : "false"}
                      onChange={e => setRationaleQuestion2(e.target.value === "" ? null : e.target.value === "true")}
                      id="exception__rationale-question-2-select"
                      testId="exception__rationale-question-2-select"
                      label=""
                      required={false}
                      isValid={!!rationaleQuestion2}
                      allowEmpty={true}
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </QESSelect>
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-9">
                    <label htmlFor="exception__rationale-question-3-select">
                      <strong>Question 3:</strong> Did the applicant contract with providers/facilities who are outside CMS’s current network adequacy criteria?
                    </label>
                  </div>
                  <div className="col-3 d-flex justify-content-end">
                    <QESSelect
                      value={rationaleQuestion3 == null ? "" : rationaleQuestion3 ? "true" : "false"}
                      onChange={e => setRationaleQuestion3(e.target.value === "" ? null : e.target.value === "true")}
                      id="exception__rationale-question-3-select"
                      testId="exception__rationale-question-3-select"
                      label=""
                      required={false}
                      isValid={!!rationaleQuestion3}
                      allowEmpty={true}
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </QESSelect>
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-9">
                    <label htmlFor="exception__rationale-question-4-select">
                      <strong>Question 4:</strong> Are there other non-contracted providers/ facilities outside CMS’s current network adequacy criteria who are located closer to plan enrollees?
                    </label>
                  </div>
                  <div className="col-3 d-flex justify-content-end">
                    <QESSelect
                      value={rationaleQuestion4 == null ? "" : rationaleQuestion4 ? "true" : "false"}
                      onChange={e => setRationaleQuestion4(e.target.value === "" ? null : e.target.value === "true")}
                      id="exception__rationale-question-4-select"
                      testId="exception__rationale-question-4-select"
                      label=""
                      required={false}
                      isValid={!!rationaleQuestion4}
                      allowEmpty={true}
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </QESSelect>
                  </div>
                </div>
              </>
            )}
            {isCciio && (
              <>
                <div className="row mb-2">
                  <div className="col-12">
                    <label htmlFor="exception__new-provider-sources-input">
                      What sources do you use to monitor for new providers entering your service area? (enter all that apply). Sources must be separated with a comma.&nbsp;
                      <TooltipWrapper text={`Required:\nEnter source(s) such as Physician Compare, Hospital Compare, NPPES, State Resources, Credentialing Organization, Direct Outreach, or Other (enter source). Separate sources with a comma.`}>
                        <QesIcon iconName={QesIcons.InfoCircle} description="Approved sources" width="16" />
                      </TooltipWrapper>
                    </label>
                  </div>
                  <div className="col-12">
                    <QESTextInput
                      value={newProviderSources}
                      onChange={(e) => setNewProviderSources(e.target.value)}
                      id="exception__new-provider-sources-input"
                      testId="exception__new-provider-sources-input"
                      label=""
                      placeholder="Enter sources, separated by commas"
                      required={false}
                      isValid={!!newProviderSources}
                    />
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-9">
                    <label htmlFor="exception__new-provider-frequency-select">
                      How often do you monitor your sources for new providers entering your service area?
                    </label>
                  </div>
                  <div className="col-3 d-flex justify-content-end">
                    <QESSelect
                      value={newProviderFrequency ?? ""}
                      onChange={(e) => setNewProviderFrequency(e.target.value === "" ? null : e.target.value as CciioNewProviderFrequency)}
                      id="exception__new-provider-frequency-select"
                      testId="exception__new-provider-frequency-select"
                      label=""
                      required={false}
                      isValid={!!newProviderFrequency}
                      allowEmpty={true}
                    >
                      <option value={CciioNewProviderFrequency.Daily}>Daily</option>
                      <option value={CciioNewProviderFrequency.Weekly}>Weekly</option>
                      <option value={CciioNewProviderFrequency.Monthly}>Monthly</option>
                      <option value={CciioNewProviderFrequency.EveryOtherMonth}>Every Other Month</option>
                      <option value={CciioNewProviderFrequency.Quarterly}>Quarterly</option>
                    </QESSelect>
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-9">
                    <label htmlFor="exception__qhp-enrollees-responsibility-check-select">
                      Do you hold QHP enrollees of this plan responsible for only in-network cost sharing for out-of-network care received when you do not meet the network adequacy standards for a network/county/specialty combination?
                    </label>
                  </div>
                  <div className="col-3 d-flex justify-content-end">
                    <QESSelect
                      value={qhpEnrolleesResponsibilityCheck == null ? "" : qhpEnrolleesResponsibilityCheck ? "true" : "false"}
                      onChange={(e) => setQhpEnrolleesResponsibilityCheck(e.target.value === "" ? null : e.target.value === "true")}
                      id="exception__qhp-enrollees-responsibility-check-select"
                      testId="exception__qhp-enrollees-responsibility-check-select"
                      label=""
                      required={false}
                      isValid={!!qhpEnrolleesResponsibilityCheck}
                      allowEmpty={true}
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </QESSelect>
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-9">
                    <label htmlFor="exception__qhp-enrollee-complaints-input">
                      What is the number of QHP enrollee complaints received regarding network adequacy during the prior 12 months?
                    </label>
                  </div>
                  <div className="col-3 d-flex justify-content-end">
                    <div className="generate-document-flyout__number-input">
                      <QESTextInput
                        value={qhpEnrolleeComplaints}
                        onChange={(e) => setQhpEnrolleeComplaints(sanitizeIntegerInput(e.target.value))}
                        onKeyDown={blockNonIntegerKeys}
                        onPaste={(e) => { e.preventDefault(); setQhpEnrolleeComplaints(sanitizeIntegerInput(e.clipboardData.getData("text"))); }}
                        id="exception__qhp-enrollee-complaints-input"
                        testId="exception__qhp-enrollee-complaints-input"
                        label=""
                        type="number"
                        min="0"
                        step="1"
                        required={false}
                        isValid={!!qhpEnrolleeComplaints && isQhpComplaintsValid}
                        unrequiredValidation={!!qhpEnrolleeComplaints}
                        errorText="Please enter a whole number (0 or greater)"
                      />
                    </div>
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-12">
                    <label htmlFor="exception__out-of-network-policy-url-input">
                      Paste the URL that explains your Out-of-Network Policy.
                    </label>
                  </div>
                  <div className="col-12">
                    <QESTextInput
                      value={outOfNetworkPolicyUrl}
                      onChange={(e) => setOutOfNetworkPolicyUrl(e.target.value)}
                      id="exception__out-of-network-policy-url-input"
                      testId="exception__out-of-network-policy-url-input"
                      label=""
                      placeholder="https://"
                      required={false}
                      isValid={!!outOfNetworkPolicyUrl && isOutOfNetworkUrlValid}
                      unrequiredValidation={!!outOfNetworkPolicyUrl}
                      errorText="Please enter a valid URL (e.g. https://example.com)"
                    />
                  </div>
                </div>
              </>
            )}
            <br />
            <QESCheckbox
              label="Alert me when the report is ready to view"
              checked={emailOnSuccess}
              onChange={(e) => setEmailOnSuccess((e.target as HTMLInputElement).checked)}
              id="exception__download-all-flyout__email-me"
              testId="exception__download-all-flyout__email-me"
            />
            <br />
            <QESCheckbox
                  label="Include non-Excluded Providers with 0% Gains into the export"
                  checked={includeZeroPercentGainsProvidersData}
                  onChange={(e) => setIncludeZeroPercentGainsProvidersData((e.target as HTMLInputElement).checked)}
                  id="exception__download-all-flyout__include-zero-percentage-gains"
                  testId="exception__download-all-flyout__include-zero-percentage-gains"
                />
                <br />
                <QESTextArea
                value={zeroPercentGainsProvidersComment}
                onChange={(e) => setZeroPercentGainsProvidersComment(e.target.value)}
                placeholder="Enter Comment for non-Excluded 0% Gains Providers"
                label="Comment to be added to the Exception export for the non-Excluded 0% Gains Providers"
                testId="exception__download-all-flyout__zero-percentage-gains-comment"
                disabled={!includeZeroPercentGainsProvidersData}
                required={includeZeroPercentGainsProvidersData}
                isValid={!!zeroPercentGainsProvidersComment}
                />
          </div>
        </div>
      </div>

      <FlyoutFooter
        submitButton={
          <QESButton
            qesType="primary"
            qesTestId="generate-document-flyout__save"
            disabled={!selectedDocumentType || !zipFileName || !submitCriteriaCheckPassed
              || (includeZeroPercentGainsProvidersData && !zeroPercentGainsProvidersComment)
              || (isCciio && (!isQhpComplaintsValid || !isOutOfNetworkUrlValid))}
            onClick={handleGenerateDocument}
          >
            Confirm
          </QESButton>
        }
      />
    </>
  );
};

export default GenerateDocumentFlyout;
